import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Trash2, RefreshCw, AlertTriangle, CheckCircle2, Eye, X, Loader2, Database } from 'lucide-react';
import { formatNyDateTime } from '../../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });

/**
 * Card no Admin/Sistema pra detectar e limpar duplicatas.
 * Duas categorias:
 * 1. Duplicatas físicas (mesmo UUID `id`, corrupção de importação) — cleanup direto
 * 2. Duplicatas de conteúdo (bills diferentes com mesmo job/data/valor) — modal de preview
 */
const DuplicateLaborBillsCleanup = () => {
  const [loading, setLoading] = useState(true);
  const [contentCount, setContentCount] = useState(null);
  const [physicalCount, setPhysicalCount] = useState(null);
  const [cleaningPhysical, setCleaningPhysical] = useState(false);
  const [cleaningContent, setCleaningContent] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadCounts = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/admin/cleanup/duplicate-labor-bills/count`, { headers: authHeaders() }),
        axios.get(`${API}/admin/cleanup/physical-id-duplicates/count`, { headers: authHeaders() }),
      ]);
      setContentCount(c.data);
      setPhysicalCount(p.data);
    } catch (e) {
      toast.error('Falha ao contar duplicatas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const cleanPhysical = async () => {
    if (!physicalCount || physicalCount.groups_found === 0) return;
    if (!window.confirm(`Confirmar limpeza de ${physicalCount.groups_found} grupos de duplicatas físicas (${physicalCount.duplicates_to_delete} docs extras)?\nMantém 1 doc por UUID.`)) return;
    setCleaningPhysical(true);
    try {
      const { data } = await axios.post(`${API}/admin/cleanup/physical-id-duplicates`, {}, { headers: authHeaders() });
      toast.success(`✅ ${data.total_deleted} docs físicos duplicados removidos`);
      await loadCounts();
    } catch (e) {
      toast.error('Falha ao limpar duplicatas físicas');
    } finally {
      setCleaningPhysical(false);
    }
  };

  const openPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await axios.get(`${API}/admin/cleanup/duplicate-labor-bills/preview`, { headers: authHeaders() });
      setPreviewData(data);
      setExcludedIds(new Set());
      setPreviewOpen(true);
    } catch (e) {
      toast.error('Falha ao carregar preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleExcluded = (id) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runContentCleanup = async () => {
    if (!previewData) return;
    // Conta quantos serão realmente deletados (todos os "não is_keep_suggestion" que não estão em excludedIds)
    const toDeleteCount = previewData.groups.reduce((sum, g) => {
      return sum + g.bills.filter(b => !b.is_keep_suggestion && !excludedIds.has(b.id)).length;
    }, 0);
    if (toDeleteCount === 0) {
      toast.info('Nenhum bill selecionado pra deletar');
      return;
    }
    if (!window.confirm(`Confirmar? ${toDeleteCount} Labor Bills serão marcados como deletados. Esta ação é reversível pelo suporte, mas não fácil.`)) return;
    setCleaningContent(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/cleanup/duplicate-labor-bills`,
        { excluded_ids: Array.from(excludedIds) },
        { headers: authHeaders() }
      );
      toast.success(`✅ ${data.total_deleted} duplicatas limpas${data.excluded_count > 0 ? ` (${data.excluded_count} preservados)` : ''}`);
      setPreviewOpen(false);
      await loadCounts();
    } catch (e) {
      toast.error('Falha ao limpar');
    } finally {
      setCleaningContent(false);
    }
  };

  const hasPhysical = physicalCount && physicalCount.groups_found > 0;
  const hasContent = contentCount && contentCount.groups_found > 0;
  const allClean = !hasPhysical && !hasContent && !loading;

  return (
    <>
      <div className={`border rounded-lg p-4 mb-4 ${allClean ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`} data-testid="dup-cleanup-card">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-2">
            {allClean ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <h4 className={`font-semibold ${allClean ? 'text-green-800' : 'text-red-800'}`}>
                🛡️ Limpeza de Duplicatas de Labor Bills
              </h4>
              <p className="text-sm text-gray-700 mt-1">
                Detecta 2 tipos: <strong>físicas</strong> (mesmo UUID por corrupção de importação) e <strong>de conteúdo</strong> (mesmo job+data+valor+PM).
              </p>
            </div>
          </div>
          <Button onClick={loadCounts} disabled={loading} size="sm" variant="outline" title="Atualizar">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {loading && contentCount === null ? (
          <p className="text-sm text-gray-500 italic">Verificando...</p>
        ) : allClean ? (
          <p className="text-green-800 font-medium">Nenhuma duplicata encontrada 🎉</p>
        ) : (
          <div className="space-y-2">
            {/* Físicas */}
            {hasPhysical && (
              <div className="bg-white border border-orange-300 rounded p-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-orange-600" />
                  <div className="text-sm">
                    <span className="font-bold text-orange-700 text-lg">{physicalCount.groups_found}</span>
                    <span className="text-gray-700"> grupos de duplicatas <strong>físicas</strong> — </span>
                    <span className="font-semibold text-orange-700">{physicalCount.duplicates_to_delete}</span>
                    <span className="text-gray-700"> docs extras</span>
                    <span className="text-xs text-gray-500 block">Corrupção de importação: mesmo UUID `id` armazenado 2x no MongoDB</span>
                  </div>
                </div>
                <Button onClick={cleanPhysical} disabled={cleaningPhysical} className="bg-orange-600 hover:bg-orange-700 text-white" size="sm">
                  {cleaningPhysical ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
                  Limpar físicas
                </Button>
              </div>
            )}
            {/* Conteúdo */}
            {hasContent && (
              <div className="bg-white border border-red-300 rounded p-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <div className="text-sm">
                    <span className="font-bold text-red-700 text-lg">{contentCount.groups_found}</span>
                    <span className="text-gray-700"> grupos de duplicatas <strong>de conteúdo</strong> — </span>
                    <span className="font-semibold text-red-700">{contentCount.duplicates_to_delete}</span>
                    <span className="text-gray-700"> bills extras</span>
                    <span className="text-xs text-gray-500 block">Bills com mesmo job+data+valor+PM (revise antes de limpar)</span>
                  </div>
                </div>
                <Button onClick={openPreview} disabled={loadingPreview} className="bg-red-600 hover:bg-red-700 text-white" size="sm">
                  {loadingPreview ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
                  Revisar e Limpar
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de preview */}
      {previewOpen && previewData && (
        <div
          className="fixed inset-0 bg-black/60 z-[100000] flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewOpen(false); }}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b bg-red-50">
              <div>
                <h3 className="text-lg font-bold text-red-800">🛡️ Revisar duplicatas antes de limpar</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {previewData.groups_found} grupos, {previewData.total_extras} bills extras. Marque os que você quer <strong>preservar</strong> (não deletar).
                </p>
              </div>
              <button onClick={() => setPreviewOpen(false)} className="p-1 rounded hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {previewData.groups.map((g, gi) => (
                <div key={gi} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 flex justify-between">
                    <span>Grupo {gi + 1} · <span className="text-blue-700">{g.key_type}</span> · {g.key} · <span className="text-red-700">{g.count} bills</span></span>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left w-24">Ação</th>
                        <th className="px-2 py-1 text-left">Job</th>
                        <th className="px-2 py-1 text-left w-24">Data</th>
                        <th className="px-2 py-1 text-left w-20">Order#</th>
                        <th className="px-2 py-1 text-left w-16">PM</th>
                        <th className="px-2 py-1 text-right w-20">Total</th>
                        <th className="px-2 py-1 text-left w-32">Criado</th>
                        <th className="px-2 py-1 text-left w-32">ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.bills.map((b, bi) => {
                        const isKeep = b.is_keep_suggestion;
                        const isExcluded = excludedIds.has(b.id);
                        const willDelete = !isKeep && !isExcluded;
                        return (
                          <tr key={bi} className={`border-t ${isKeep ? 'bg-green-50' : willDelete ? 'bg-red-50' : 'bg-blue-50'}`}>
                            <td className="px-2 py-1">
                              {isKeep ? (
                                <span className="text-xs font-semibold text-green-700">✓ Manter</span>
                              ) : (
                                <label className="inline-flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isExcluded}
                                    onChange={() => toggleExcluded(b.id)}
                                    data-testid={`preserve-${b.id}`}
                                  />
                                  <span className={`text-xs ${isExcluded ? 'text-blue-700 font-semibold' : 'text-red-700'}`}>
                                    {isExcluded ? 'Preservar' : 'Deletar'}
                                  </span>
                                </label>
                              )}
                            </td>
                            <td className="px-2 py-1 truncate max-w-[200px]">{b.job_name || '—'}</td>
                            <td className="px-2 py-1">{b.install_date || '—'}</td>
                            <td className="px-2 py-1">{b.order_number || '—'}</td>
                            <td className="px-2 py-1">{b.pm_code || '—'}</td>
                            <td className="px-2 py-1 text-right font-mono">${(b.grand_total || 0).toFixed(2)}</td>
                            <td className="px-2 py-1 text-gray-500">{b.created_at ? formatNyDateTime(b.created_at) : '—'}</td>
                            <td className="px-2 py-1 text-gray-400 font-mono">{(b.id || '').slice(0, 8)}…</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>

            <div className="p-4 border-t bg-gray-50 flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-gray-600">
                <span className="text-red-700 font-semibold">
                  {previewData.groups.reduce((s, g) => s + g.bills.filter(b => !b.is_keep_suggestion && !excludedIds.has(b.id)).length, 0)}
                </span> serão deletados · <span className="text-blue-700 font-semibold">{excludedIds.size}</span> preservados
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={cleaningContent}>Cancelar</Button>
                <Button onClick={runContentCleanup} disabled={cleaningContent} className="bg-red-600 hover:bg-red-700 text-white">
                  {cleaningContent ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Limpando...</> : <><Trash2 className="h-4 w-4 mr-1.5" /> Confirmar Limpeza</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DuplicateLaborBillsCleanup;
